import { apiFetch } from '@/data/api';
import { connectionsPayload, type ClientConnections, type ConnectionsPayload, type Manager } from '@/data/client';
import type { AdminClient } from './types';

export interface AdminClientsResponse {
  clients: AdminClient[];
  managers: Manager[];
  dataset: string;
}

/* One fetch, one cache key — the clients list and the client page both read
   ['admin', 'clients'], so opening a client from the list is instant and a save on
   the page refreshes the list behind it with a single invalidation. */
export const ADMIN_CLIENTS_KEY = ['admin', 'clients'] as const;
export function getAdminClients(): Promise<AdminClientsResponse> {
  return apiFetch<AdminClientsResponse>('/api/admin/clients');
}

/** The full-screen client page, with the tab carried in the URL like trafficIQ. */
export function adminClientPath(orgId: string, tab = 'profile'): string {
  return `/admin/clients/${encodeURIComponent(orgId)}?tab=${encodeURIComponent(tab)}`;
}

/* The form-shaped view of a client's connections — strings for every input so a
   half-typed number is not coerced under the cursor. Was the settings dialog's
   `initialConn`; now every tab form starts from it and edits only its own keys. */
export function connectionsFromClient(client: AdminClient): ClientConnections {
  return {
    bqTable: client.bqTable ?? '',
    googleAdsCustomerId: client.googleAdsCustomerId ?? '',
    ga4PropertyId: client.ga4PropertyId ?? '',
    gscSiteUrl: client.gscSiteUrl ?? '',
    googleSa: client.googleSa === '2' ? '2' : '1',
    gatherupClientId: client.gatherupClientId ?? '',
    reviewsStaleDays: String(client.reviewsStaleDays ?? 30),
    monthlyAdSpendGoal: client.monthlyAdSpendGoal != null ? String(client.monthlyAdSpendGoal) : '',
    adBillingDay: client.adBillingDay != null ? String(client.adBillingDay) : '',
    contentReminderEnabled: client.contentReminderEnabled,
    contentReminderDays: String(client.contentReminderDays ?? 30),
    contentReminderInterval: String(client.contentReminderInterval ?? 7),
    aiInterviewEnabled: client.aiInterviewEnabled,
    aiImagesEnabled: client.aiImagesEnabled,
    interviewStyle: client.interviewStyle ?? 'guided',
    contentManagerId: client.contentManagerId ?? '',
    team: {
      contentEditor: client.team?.contentEditor ?? '',
      accountManager: client.team?.accountManager ?? '',
      optimizationManager: client.team?.optimizationManager ?? '',
      paidAdsManager: client.team?.paidAdsManager ?? '',
      brandStrategyManager: client.team?.brandStrategyManager ?? '',
      designDevManager: client.team?.designDevManager ?? '',
    },
    selfServiceEnabled: client.selfServiceEnabled,
    faqPushEnabled: client.faqPushEnabled ?? false,
    faqReviewHold: client.faqReviewHold ?? true,
    faqChatEnabled: client.faqChatEnabled ?? false,
    trafficCompareEnabled: client.trafficCompareEnabled,
    excludedCampaignIds: client.excludedCampaignIds ?? [],
    isDemo: client.isDemo,
  };
}

export type ConnKey = keyof ClientConnections;

/* Only the keys a tab owns go over the wire. Payload keys share names with the
   form keys, so the pick is by name. */
export function pickConnections(draft: ClientConnections, keys: readonly ConnKey[]): Partial<ConnectionsPayload> {
  const full = connectionsPayload(draft) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = full[k];
  return out as Partial<ConnectionsPayload>;
}

/* Which tab owns which connection field. One place, so a test can prove every
   field belongs to exactly one tab: a field missing here would render nowhere
   and could never be saved, and a field in two tabs would let one tab silently
   overwrite an edit made on the other. */
export const CONNECTION_TABS = {
  leads: ['bqTable'],
  ppc: ['googleAdsCustomerId', 'monthlyAdSpendGoal', 'adBillingDay', 'excludedCampaignIds'],
  traffic: ['ga4PropertyId', 'gscSiteUrl', 'googleSa', 'gatherupClientId', 'reviewsStaleDays', 'trafficCompareEnabled'],
  content: [
    'aiInterviewEnabled', 'aiImagesEnabled', 'interviewStyle',
    'contentReminderEnabled', 'contentReminderDays', 'contentReminderInterval',
    'selfServiceEnabled',
    'faqPushEnabled',
    'faqReviewHold',
    'faqChatEnabled',
  ],
  // Which Triton people run the account — lives with the client's own users
  // because "who is on this client" is one question, not two tabs.
  team: ['contentManagerId', 'team'],
  demo: ['isDemo'],
} as const satisfies Record<string, readonly ConnKey[]>;
